import { formatUnits } from '@ethersproject/units';
import { FC, useCallback } from 'react';
import useNotify from '../../../hooks/useNotify';
import useSiloRouter from '../../../hooks/useSiloRouter';
import {
  ActionForm,
  DecideIsFullAmount,
  DoApprove,
  DoSubmit,
  QueryApproval,
  SubmitButtonContent,
  ValidateForm,
} from '../common/ActionForm';
import { FULL_AMOUNT_THRESHOLD } from 'app/config/networks';
import { useThisDecoratedSilo } from '../silo';
import { getDebtAssets, Recipe } from 'queries/decoratedSilo';
import { useRouterSelectedAddress } from '../common/ActionForm/useRouterSelectedAddress';
import { useClearAssetFromQueryString } from '../silo/hooks';
import { asDecimal, handleParsedError, parseUserInitiatedTransactionError } from '@silo/utils';
import { BIG_ZERO } from 'app/constants';
import { GetNewLTV, HealthFactorConfig } from '../common/HealthFactor';
import produce from 'immer';
import { calculateLTV, getAssociatedAsset, getCountOfActiveDebtAssets } from 'app/features/util/ltv';
import { useAddTransaction, useSiloWeb3 } from '@silo/web3';
import { GetNewUtilization } from '../../../hooks/useUtilizationImpact';
import { calculateUtilization } from 'app/functions/silo/calculations';
import posthog from 'posthog-js';
import { useFinishTransaction } from '../silo/useFinishTransaction';
import { useIsAssetActionFrozen } from '../common/ActionForm/useIsFrozen';
import { GetMaxAmount } from 'app/hooks/useMaxTokenAmount';
import { RepayMaxAmount } from './RepayMaxAmount';
import { ProtocolInstance, createBigNumberWithDecimals } from '@silo/types';

export type DoRepay = DoSubmit;

export const useDoRepay = (): DoRepay => {
  const { siloAddress } = useThisDecoratedSilo();
  const { routerRepay, routerRepayAll } = useSiloRouter();
  const addTransaction = useAddTransaction();
  const { notifyError, notifyWarning } = useNotify();
  const { account } = useSiloWeb3();
  const finishTransaction = useFinishTransaction();

  return useCallback(
    async ({ asset, amount, isFullAmount }) => {
      if (!account) {
        throw new Error('No user account');
      }

      try {
        const repay = async () => {
          if (isFullAmount) {
            return {
              tx: await routerRepayAll(siloAddress, asset.assetAddress, account),
              message: `Repaid full balance of ${asset.symbol}`,
            };
          }

          const formattedAmount = asDecimal(formatUnits(amount, asset.decimals));

          return {
            tx: await routerRepay(siloAddress, asset.assetAddress, amount),
            message: `Repaid ${formattedAmount} ${asset.symbol}`,
          };
        };

        const { tx, message } = await repay();

        addTransaction(tx, message);
        posthog.capture('repay', { siloAddress, assetAddress: asset.assetAddress, amount, isFullAmount });

        const { status } = await tx.wait();

        if (status === 0) {
          throw new Error(`Transaction ${tx.hash} reverted`);
        }

        finishTransaction(getRepayRecipe({ asset, amount, isFullAmount }));
      } catch (err) {
        console.error('Error on repay:,', err);
        const parsed = parseUserInitiatedTransactionError(err);

        const captureAndNotify = (message: string) => {
          notifyError(message);
        };

        const { rethrow } = handleParsedError({
          onWarning: notifyWarning,
          onError: captureAndNotify,
          onUnparsed: captureAndNotify,
        })(parsed);

        // re-throw error to be handled by react-query
        if (rethrow) throw err;
      }
    },
    [routerRepay, routerRepayAll, account, notifyWarning, siloAddress, addTransaction, notifyError, finishTransaction]
  );
};

type GetRepayRecipe = (...params: Parameters<DoRepay>) => Recipe;

const getRepayRecipe: GetRepayRecipe =
  ({ asset, amount, isFullAmount }) =>
  (draft) => {
    const assetToUpdate = draft.assets.find(({ assetAddress }) => asset.assetAddress === assetAddress);

    if (!assetToUpdate) return;

    const {
      totalBorrowAmount: previousTotalBorrowAmount,
      debtBalance: previousDebtBalance,
      projectedDebtBalance: previousProjectedDebtBalance,
      userBalance: previousUserBalance,
    } = assetToUpdate;

    if (isFullAmount) {
      assetToUpdate.totalBorrowAmount = previousTotalBorrowAmount.sub(previousDebtBalance);
      assetToUpdate.debtBalance = BIG_ZERO;
      assetToUpdate.projectedDebtBalance = BIG_ZERO;
      assetToUpdate.userBalance = previousUserBalance.sub(previousDebtBalance);
    } else {
      assetToUpdate.totalBorrowAmount = previousTotalBorrowAmount.sub(amount);
      assetToUpdate.debtBalance = previousDebtBalance.sub(amount);
      // just subtracting for now - could optimize by adding a proportional amount of interest
      assetToUpdate.projectedDebtBalance = previousProjectedDebtBalance.sub(amount);
      assetToUpdate.userBalance = previousUserBalance.sub(amount);
    }
  };

const getNewLTV: GetNewLTV = ({ currentLTV: { args }, normalizedTokenAmount, selectedAsset }) => {
  const newArgs = produce(args, (draft) => {
    const associatedAsset = getAssociatedAsset(draft.debtAssets, selectedAsset);

    if (associatedAsset) {
      associatedAsset.balance -= normalizedTokenAmount.toBigInt();
    } else {
      console.warn(`siloAsset with address ${selectedAsset.assetAddress} could not be found in the asset array`);
    }
  });

  return calculateLTV(newArgs);
};

const validateForm: ValidateForm = ({ normalizedTokenAmount, maxAmount, selectedAsset, isFullAmount }) => {
  const trueAmount = isFullAmount ? selectedAsset.projectedDebtBalance : normalizedTokenAmount ?? BIG_ZERO;

  if (trueAmount.gt(selectedAsset.userBalance)) {
    return {
      message: `You don't have enough ${selectedAsset.symbol}`,
      submitMessage: `Insufficient ${selectedAsset.symbol} balance`,
    };
  }

  if (trueAmount.gt(maxAmount.value.bigNumber)) {
    return { message: 'You cannot repay more than you have borrowed.' };
  }

  return null;
};

const decideIsFullAmount: DecideIsFullAmount = ({ amountInUSD, maxAmountInUSD }) => {
  if (!amountInUSD || !maxAmountInUSD) {
    return false;
  }

  if (amountInUSD.bigNumber.gt(maxAmountInUSD.bigNumber)) {
    return false;
  }

  const difference = maxAmountInUSD.bigNumber.sub(amountInUSD.bigNumber);

  return difference.lt(FULL_AMOUNT_THRESHOLD.bigNumber);
};

const getMaxAmount: GetMaxAmount = ({ selectedAsset: { projectedDebtBalance, decimals } }) => {
  return {
    type: 'protocolBalance',
    value: createBigNumberWithDecimals(projectedDebtBalance, decimals),
  };
};

const healthFactorConfig: HealthFactorConfig = {
  getTokenAdjustment: ({ action, tokens }) => {
    if (action !== 'repay') return 0n;

    return tokens;
  },
  getMax: ({ riskFactor }) => riskFactor,
  setMaxAmountWhen: ({ value, LTV }) => {
    const activeDebtAssets = getCountOfActiveDebtAssets(LTV);

    return value === 0 && activeDebtAssets === 1;
  },
};

export interface RepayFormProps {
  protocolInstance: ProtocolInstance;
  queryApproval: QueryApproval;
  doApprove: DoApprove;
  doRepay: DoRepay;
}

export const RepayForm: FC<RepayFormProps> = ({ doRepay, protocolInstance, queryApproval, doApprove }) => {
  const decoratedSilo = useThisDecoratedSilo();
  const routerSelectedAddress = useRouterSelectedAddress('repay');
  const clearAssetFromQueryString = useClearAssetFromQueryString('repay');
  const isActionFrozen = useIsAssetActionFrozen('repay');

  return (
    <ActionForm
      getMaxAmount={getMaxAmount}
      maxAmountSlot={<RepayMaxAmount />}
      queryStringSelectedAddress={routerSelectedAddress}
      submitButtonContentSlot={({ selectedAsset }) => (
        <SubmitButtonContent
          submitCopy={
            isActionFrozen(selectedAsset.assetAddress, decoratedSilo.siloAddress)
              ? `Repaying ${selectedAsset.symbol} is temporarily disabled`
              : 'Repay'
          }
          submittingCopy="Repaying"
        />
      )}
      doSubmit={doRepay}
      getNewLTV={getNewLTV}
      getNewUtilization={getNewUtilization}
      assetsToDisplay={getDebtAssets(decoratedSilo)}
      protocolInstance={protocolInstance}
      requireApproval={{
        queryApproval,
        doApprove,
      }}
      validateForm={validateForm}
      data-cy="repay-form"
      disableSubmit={({ selectedAsset }) => isActionFrozen(selectedAsset.assetAddress, decoratedSilo.siloAddress)}
      decideIsFullAmount={decideIsFullAmount}
      onSelectedAddressChange={clearAssetFromQueryString}
      healthFactorConfig={healthFactorConfig}
      termsOfUseAction="repay"
    />
  );
};

const getNewUtilization: GetNewUtilization = (amount, deposits, liquidity) => {
  return calculateUtilization(deposits, deposits - liquidity - amount);
};
